import { formatSource, getProviderMetadataField } from "features/videoUploads/functions";
import { parseVideoMetadata } from "features/videoUploads/metadata/functions";
import { _ } from "i18n";
import type { GameDetail, VideoUploadDetail } from "libs/apiClient";
import client from "libs/client";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { VideoFact, VideoInfoSection } from "./details";
import { VideoProviderMetadataField } from "./types";

interface MatchAndVideoInfoTabProps {
	video: VideoUploadDetail;
}

function formatUploadedAt(createdAt: string) {
	const uploadedAt = new Date(createdAt);
	return `${uploadedAt.toLocaleDateString()} ${uploadedAt.toLocaleTimeString()}`;
}

/**
 * Parses API game dates (`Y-m-d H:i:s`). Safari's Date parser rejects that space-separated
 * form, so we build a local Date from the numeric parts instead of `new Date(date)`.
 */
function parseApiGameDate(date: string): Date | null {
	const match = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?/.exec(date);
	if (match === null) {
		return null;
	}
	const year = Number(match[1]);
	const monthIndex = Number(match[2]) - 1;
	const day = Number(match[3]);
	const hours = Number(match[4] ?? "0");
	const minutes = Number(match[5] ?? "0");
	const seconds = Number(match[6] ?? "0");
	const gameDate = new Date(year, monthIndex, day, hours, minutes, seconds);
	if (Number.isNaN(gameDate.getTime())) {
		return null;
	}
	return gameDate;
}

function formatGameInfoDate(date: string) {
	const gameDate = parseApiGameDate(date);
	if (gameDate === null) {
		return "-";
	}
	return gameDate.toLocaleDateString();
}

/** Veo Game Date display format from the linked game date. */
function formatVeoGameDate(date: string) {
	const gameDate = parseApiGameDate(date);
	if (gameDate === null) {
		return "-";
	}
	const day = String(gameDate.getDate()).padStart(2, "0");
	const month = String(gameDate.getMonth() + 1).padStart(2, "0");
	const year = String(gameDate.getFullYear()).slice(-2);
	return `${day}-${month}-${year}`;
}

function clubOrTeamName(clubName?: string | null, teamName?: string | null) {
	if (clubName != null && clubName !== "") {
		return clubName;
	}
	if (teamName != null && teamName !== "") {
		return teamName;
	}
	return "-";
}

function displayValue(value?: string | null) {
	if (value == null || value === "") {
		return "-";
	}
	return value;
}

/**
 * Display helper for stored game Veo links (typically `app.veo.co/matches/...`).
 * `getTrustedVeoLink` is only for Sportlogiq CDN `veocdn.com/.../panorama/video.ts` checks.
 */
function getDisplayableVeoLink(veoLink?: string | null): string | null {
	if (veoLink == null || veoLink === "") {
		return null;
	}

	const trimmedVeoLink = veoLink.trim();
	if (trimmedVeoLink === "") {
		return null;
	}

	try {
		const parsedUrl = new URL(trimmedVeoLink);
		if (parsedUrl.protocol !== "https:" && parsedUrl.protocol !== "http:") {
			return null;
		}
	} catch {
		return null;
	}

	return trimmedVeoLink;
}

function veoLinkFactValue(veoLink?: string | null): ReactNode {
	const displayableVeoLink = getDisplayableVeoLink(veoLink);
	if (displayableVeoLink === null) {
		return "-";
	}
	return (
		<a className="videoInfoSection-link" href={displayableVeoLink} target="_blank" rel="noopener noreferrer">
			{displayableVeoLink}
		</a>
	);
}

function gameInfoSectionContent({
	gameId,
	isGameLoading,
	game,
}: {
	gameId?: number;
	isGameLoading: boolean;
	game?: GameDetail;
}): ReactNode {
	if (gameId === undefined) {
		return <div className="videoInfoSection-empty">{_("admin.video.info.noLinkedGame")}</div>;
	}
	if (isGameLoading) {
		return <div className="videoInfoSection-empty">{_("admin.video.info.loadingGame")}</div>;
	}
	if (game == null) {
		return (
			<div className="videoInfoSection-empty" role="alert">
				{_("admin.video.info.gameUnavailable")}
			</div>
		);
	}
	return (
		<>
			<VideoFact
				label={_("admin.video.info.gameId")}
				value={
					<Link className="videoInfoSection-link" to={`/game/${game.id}`}>
						{game.id}
					</Link>
				}
			/>
			<VideoFact
				label={_("admin.video.info.homeClub")}
				value={clubOrTeamName(game.homeClubName, game.homeTeamName)}
			/>
			<VideoFact
				label={_("admin.video.info.awayClub")}
				value={clubOrTeamName(game.awayClubName, game.awayTeamName)}
			/>
			<VideoFact label={_("admin.video.info.date")} value={game.date != null ? formatGameInfoDate(game.date) : "-"} />
			<VideoFact label={_("admin.video.info.ageCategory")} value={displayValue(game.category)} />
		</>
	);
}

export function MatchAndVideoInfoTab({ video }: MatchAndVideoInfoTabProps) {
	const metadata = parseVideoMetadata(video.metadata);
	const gameId = metadata?.gameId;
	const { data: gameResponse, isLoading: isGameLoading } = client.getVideoGameDetail.useQuery(
		{ id: gameId ?? 0 },
		{ enabled: gameId !== undefined },
	);
	const game = gameResponse?.data;
	const veoClubhouse = getProviderMetadataField(
		video.providerMetadata,
		VideoProviderMetadataField.ClubhouseName
	);
	const resolution =
		video.width != null && video.height != null ? `${video.width}x${video.height}` : "-";

	return (
		<div className="matchAndVideoInfoTab">
			<VideoInfoSection title={_("admin.video.info.systemInfo")}>
				<VideoFact label={_("admin.video.info.uploadedBy")} value={displayValue(video.uploadedBy)} />
				<VideoFact
					label={_("admin.video.info.uploaded")}
					value={video.createdAt !== "" ? formatUploadedAt(video.createdAt) : "-"}
				/>
			</VideoInfoSection>

			<VideoInfoSection title={_("admin.video.info.gameInfo")}>
				{gameInfoSectionContent({ gameId, isGameLoading, game })}
			</VideoInfoSection>

			<VideoInfoSection title={_("admin.video.info.videoMetadata")}>
				<VideoFact label={_("admin.video.info.videoName")} value={displayValue(video.name)} />
				<VideoFact
					label={_("admin.video.info.source")}
					value={formatSource(video.origin, video.provider) ?? "-"}
				/>
				<VideoFact label={_("admin.video.info.resolution")} value={resolution} />
				<VideoFact label={_("admin.video.info.fileSize")} value={displayValue(video.size)} />
				<VideoFact label={_("admin.video.info.bitrate")} value={displayValue(video.bitRate)} />
				<VideoFact label={_("admin.video.info.veoLink")} value={veoLinkFactValue(game?.veoLink)} />
				<VideoFact label={_("admin.video.info.veoCameraId")} value="-" />
				<VideoFact label={_("admin.video.info.veoTeam")} value="-" />
				<VideoFact label={_("admin.video.info.veoClubhouse")} value={displayValue(veoClubhouse)} />
				<VideoFact
					label={_("admin.video.info.veoGameDate")}
					value={game?.date != null ? formatVeoGameDate(game.date) : "-"}
				/>
			</VideoInfoSection>
		</div>
	);
}
